import type { User } from "@supabase/supabase-js";
import type { createClient } from "./client";
import { isTransientDbError } from "./retry-transient";

type WebSupabaseClient = ReturnType<typeof createClient>;

export interface SafeGetUserOptions {
  retries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
}

/**
 * supabase.auth.getUser() 안전 래퍼.
 * Web Locks API 충돌과 일시적 네트워크 오류만 retry, 그 외는 throw.
 * 모든 retry 소진 시 null 반환 → 호출처의 `if (!user)` 분기로 합류.
 */
export async function safeGetUser(
  supabase: WebSupabaseClient,
  options: SafeGetUserOptions = {}
): Promise<User | null> {
  const { retries = 2, baseDelayMs = 200, maxDelayMs = 1000 } = options;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const { data } = await supabase.auth.getUser();
      return data.user;
    } catch (error) {
      if (!isRetryableAuthError(error)) throw error;
      if (attempt === retries) return null;

      const delay = Math.min(baseDelayMs * 2 ** attempt, maxDelayMs);
      const jitter = Math.random() * 100 - 50;
      await new Promise((r) => setTimeout(r, delay + jitter));
    }
  }

  return null;
}

function isRetryableAuthError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  // Auth-specific transient cases (Web Locks API contention).
  if (error.name === "NavigatorLockAcquireTimeoutError") return true;
  if (/Lock .* was released because another request stole it/.test(error.message)) return true;
  if (/Acquiring an exclusive Navigator LockManager lock/.test(error.message)) return true;
  if (error.name === "AuthRetryableFetchError") return true;
  // Shared transient-infra cases (fetch timeout/abort, connection drop). The
  // default-injected makeTimeoutFetch surfaces as TimeoutError / AbortError.
  return isTransientDbError(error);
}
